import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorHandlerService } from '../../services/error-handler.service';

@Component({
  selector: 'app-forgot-password',
  imports: [
    CommonModule,
    MatAngularModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
  standalone: true,
})
export class ForgotPasswordComponent {
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  snackBar = inject(MatSnackBar);
  errorHandler = inject(ErrorHandlerService);

  form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  isLoading = false;
  submitted = false;

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;
    try {
      await this.authService.forgotPassword(this.form.value.email).toPromise();
      this.submitted = true;
      this.snackBar.open(
        "If an account exists for this email, we've sent a reset link. Please check your inbox and spam folder.",
        'Close',
        { duration: 5000, panelClass: ['success-snackbar'] }
      );
    } catch (error: unknown) {
      const msg = this.errorHandler.extractErrorMessage(
        error,
        'Something went wrong. Please try again later.'
      );
      if (msg.includes('429') || msg.toLowerCase().includes('too many')) {
        this.snackBar.open('Too many requests. Please try again in 15 minutes.', 'Close', {
          duration: 6000,
          panelClass: ['error-snackbar'],
        });
      } else {
        this.snackBar.open(msg, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
      }
    } finally {
      this.isLoading = false;
    }
  }

  getErrorMessage(controlName: string): string {
    const c = this.form.get(controlName);
    if (c?.hasError('required')) return 'Email is required';
    if (c?.hasError('email')) return 'Please enter a valid email address';
    return '';
  }
}
